/**
 * Центр уведомлений покупателя (BR-45): новости о заказах и баллах. Сервер
 * кладёт сюда всё, что отправил покупателю, и сам считает непрочитанные.
 * Браузерного push в приложении покупателя нет — только этот список.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { nowMs, parseMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type NotificationDto = components['schemas']['Notification']

export interface Notification {
  id: number
  template: string
  title: string
  body: string
  /** Заказ, о котором новость; null — новость о баллах и бонусах. */
  orderId: number | null
  /** Когда прочитано, мс; 0 — ещё не прочитано. */
  readAt: number
  at: number
}

export interface InboxPage {
  items: Notification[]
  /** Сколько непрочитанных — сервер отдаёт их в `total` списка. */
  unread: number
}

/** Сколько уведомлений в одной странице списка. */
export const INBOX_PAGE_SIZE = 20

/** Как часто перечитывать счётчик колокольчика: push в приложении нет. */
export const UNREAD_POLL_MS = 60_000

export function toNotification(dto: NotificationDto): Notification {
  return {
    id: dto.id ?? 0,
    template: dto.template ?? '',
    title: dto.title ?? '',
    body: dto.body ?? '',
    orderId: dto.order_id ?? null,
    readAt: parseMs(dto.read_at),
    at: parseMs(dto.created_at),
  }
}

async function fetchInbox(api: ApiClient, page: number): Promise<InboxPage> {
  const dto = await api.get<{ items?: NotificationDto[]; total?: number }>('/me/notifications', {
    query: { page, page_size: INBOX_PAGE_SIZE },
  })
  return { items: (dto.items ?? []).map(toNotification), unread: dto.total ?? 0 }
}

/**
 * Список страницами, новые сверху. Заголовок и текст сервер пишет на языке
 * запроса, поэтому язык — часть ключа. Число непрочитанных из списка сразу
 * переходит в колокольчик: два числа на экране не расходятся.
 */
export function useInbox(signedIn: boolean, lang: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useInfiniteQuery({
    queryKey: [...keys.inbox, lang],
    queryFn: async ({ pageParam }) => {
      const page = await fetchInbox(api, pageParam)
      queryClient.setQueryData(keys.unread, page.unread)
      return page
    },
    initialPageParam: 1,
    // Неполная страница — последняя: сколько всего уведомлений, сервер не говорит.
    getNextPageParam: (last, pages) =>
      last.items.length === INBOX_PAGE_SIZE ? pages.length + 1 : undefined,
    enabled: signedIn,
  })
}

/**
 * Число непрочитанных для бейджа. Перечитывается само и при возврате во
 * вкладку (живого канала у покупателя нет — только опрос и push). Выросло —
 * пришло новое: перечитываем и список, иначе открытая страница уведомлений
 * показывала бы новый бейдж без новой строки.
 */
export function useUnreadCount(signedIn: boolean) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: keys.unread,
    queryFn: async () => {
      const before = queryClient.getQueryData<number>(keys.unread)
      const count = (await api.get<{ count?: number }>('/me/notifications/unread-count')).count ?? 0
      if (before !== undefined && count > before) {
        void queryClient.invalidateQueries({ queryKey: keys.inbox })
        // Кэшбэк и бонусы тоже приходят уведомлением — баланс мог вырасти.
        void queryClient.invalidateQueries({ queryKey: keys.loyalty })
      }
      return count
    },
    enabled: signedIn,
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: true,
  })
}

/** Отметить прочитанными эти уведомления; пустой список — все сразу. */
export function markRead(pages: InboxPage[], ids: number[], at: number): InboxPage[] {
  const all = ids.length === 0
  return pages.map((page) => ({
    ...page,
    items: page.items.map((n) =>
      n.readAt === 0 && (all || ids.includes(n.id)) ? { ...n, readAt: at } : n,
    ),
  }))
}

/**
 * «Прочитано». Список и бейдж меняются сразу, не дожидаясь сервера; после
 * ответа (или ошибки) оба перечитываются — правда остаётся за сервером.
 */
export function useMarkRead() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => api.post('/me/notifications/read', { ids }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: keys.notifications })
      const at = nowMs()
      queryClient.setQueriesData<InfiniteData<InboxPage>>(
        { queryKey: keys.inbox },
        (data) => data && { ...data, pages: markRead(data.pages, ids, at) },
      )
      // Экран отмечает по одной непрочитанной новости или все сразу.
      queryClient.setQueryData<number>(keys.unread, (count = 0) =>
        ids.length === 0 ? 0 : Math.max(0, count - ids.length),
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.notifications }),
  })
}
